import React from 'react';

/**
 * Card component props interface
 * Extends HTML div attributes for full compatibility
 */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Visual variant of the card */
  variant?: 'default' | 'elevated' | 'flat';
  /** Padding size inside the card */
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** Card content */
  children: React.ReactNode;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
  /** Whether the card is interactive (adds hover effects) */
  interactive?: boolean;
}

/**
 * Design system colors and styles
 */
const COLORS = {
  white: 'var(--surface-surface_0)',
  border: 'var(--outline-base_em)',
  shadow: 'rgba(0, 0, 0, 0.1)',
  shadowStrong: 'rgba(0, 0, 0, 0.15)',
} as const;

/**
 * Card component following design system standards
 * Provides a flexible container with consistent styling
 * 
 * @example
 * ```tsx
 * <Card variant="elevated" padding="md">
 *   <h3>Card Title</h3>
 *   <p>Card content goes here</p>
 * </Card>
 * 
 * <Card variant="outlined" interactive onClick={handleClick}>
 *   <p>Clickable card</p>
 * </Card>
 * ```
 */
const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  children,
  className,
  style,
  interactive = false,
  ...props
}) => {
  const getCardStyles = (): React.CSSProperties => {
    const baseStyles: React.CSSProperties = {
      backgroundColor: COLORS.white,
      borderRadius: '12px',
      transition: 'all 0.15s ease-in-out',
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
    };

    const variantStyles: Record<NonNullable<CardProps['variant']>, React.CSSProperties> = {
      default: {
        border: `1px solid ${COLORS.border}`,
        boxShadow: `0 1px 3px ${COLORS.shadow}`,
      },
      elevated: {
        border: `1px solid ${COLORS.border}`,
        boxShadow: `0 4px 6px -1px ${COLORS.shadow}, 0 2px 4px -1px ${COLORS.shadow}`,
      },
      flat: {
        border: 'none',
        boxShadow: 'none',
      },
    };

    const paddingStyles: Record<NonNullable<CardProps['padding']>, React.CSSProperties> = {
      none: { padding: '0' },
      sm: { padding: '16px' },
      md: { padding: '24px' },
      lg: { padding: '32px' },
    };

    const interactiveStyles: React.CSSProperties = interactive ? {
      cursor: 'pointer',
    } : {};

    return {
      ...baseStyles,
      ...variantStyles[variant],
      ...paddingStyles[padding],
      ...interactiveStyles,
      ...style,
    };
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    if (interactive) {
      const target = e.currentTarget;
      if (variant === 'elevated') {
        target.style.boxShadow = `0 8px 25px -5px ${COLORS.shadowStrong}, 0 4px 6px -2px ${COLORS.shadow}`;
        target.style.transform = 'translateY(-2px)';
      } else {
        target.style.boxShadow = `0 4px 6px -1px ${COLORS.shadow}, 0 2px 4px -1px ${COLORS.shadow}`;
        target.style.transform = 'translateY(-1px)';
      }
    }
    
    // Call original onMouseEnter if provided
    if (props.onMouseEnter) {
      props.onMouseEnter(e);
    }
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (interactive) {
      const target = e.currentTarget;
      target.style.transform = 'translateY(0)';
      
      // Reset to original shadow based on variant
      if (variant === 'elevated') {
        target.style.boxShadow = `0 4px 6px -1px ${COLORS.shadow}, 0 2px 4px -1px ${COLORS.shadow}`;
      } else if (variant === 'default') {
        target.style.boxShadow = `0 1px 3px ${COLORS.shadow}`;
      } else {
        target.style.boxShadow = 'none';
      }
    }
    
    // Call original onMouseLeave if provided
    if (props.onMouseLeave) {
      props.onMouseLeave(e);
    }
  };

  return (
    <div
      style={getCardStyles()}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </div>
  );
};

Card.displayName = 'Card';

export default Card;
